import React, { useState, useMemo } from "react";
import { X, RotateCcw, Info } from "lucide-react";
import { useAllFeatureToggles } from "@hooks/useFeatureToggle";
import {
  FEATURE_TOGGLES,
  FEATURE_GROUPS,
  featureToggleManager,
  FeatureToggleKey,
} from "../../lib/featureToggles";
import "./FeatureToggleDebugPanel.css";

interface FeatureToggleDebugPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

// Debounce hook
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  React.useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

export const FeatureToggleDebugPanel: React.FC<
  FeatureToggleDebugPanelProps
> = ({ isOpen, onClose }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedGroup, setSelectedGroup] = useState<string>("all");
  const allFeatureStates = useAllFeatureToggles();

  // Debug: log all feature states when they change
  React.useEffect(() => {
    console.log("All feature states in debug panel:", allFeatureStates);
  }, [allFeatureStates]);

  // Debounce search term to avoid re-render spam (200ms)
  const debouncedSearchTerm = useDebounce(searchTerm, 200);

  // Group features by their group for better organization
  const groupedFeatures = useMemo(() => {
    const grouped: Record<
      string,
      Array<{
        key: FeatureToggleKey;
        meta: (typeof FEATURE_TOGGLES)[FeatureToggleKey];
        enabled: boolean;
      }>
    > = {};

    Object.entries(FEATURE_TOGGLES).forEach(([key, meta]) => {
      const featureKey = key as FeatureToggleKey;
      const group = meta.group;

      if (!grouped[group]) {
        grouped[group] = [];
      }

      grouped[group].push({
        key: featureKey,
        meta,
        enabled: allFeatureStates[meta.key] ?? meta.default,
      });
    });

    return grouped;
  }, [allFeatureStates]);

  // Filter features based on search and group selection (using debounced search)
  const filteredFeatures = useMemo(() => {
    let features = Object.entries(groupedFeatures);

    // Filter by group
    if (selectedGroup !== "all") {
      features = features.filter(([group]) => group === selectedGroup);
    }

    // Filter by search term (debounced)
    if (debouncedSearchTerm) {
      features = features
        .map(([group, items]) => {
          const filteredItems = items.filter(
            (item) =>
              item.meta.key
                .toLowerCase()
                .includes(debouncedSearchTerm.toLowerCase()) ??
              item.meta.description
                .toLowerCase()
                .includes(debouncedSearchTerm.toLowerCase()),
          );
          return [group, filteredItems] as [string, typeof items];
        })
        .filter(([_, items]) => items.length > 0);
    }

    return features;
  }, [groupedFeatures, debouncedSearchTerm, selectedGroup]);

  const handleToggleFeature = (
    featureKey: FeatureToggleKey,
    enabled: boolean,
  ) => {
    console.log(`Debug panel: toggling ${featureKey} to ${enabled}`);
    // Update the feature toggle state
    featureToggleManager.overrideFeature(featureKey, enabled);
  };

  const handleClearOverrides = () => {
    featureToggleManager.clearDebugOverrides();
  };

  if (!isOpen) return null;

  return (
    <div className="feature-toggle-debug-panel-overlay">
      <div className="feature-toggle-debug-panel">
        {/* Header */}
        <div className="feature-toggle-debug-panel__header">
          <h3>Feature Toggle Debug Panel</h3>
          <button
            onClick={onClose}
            className="feature-toggle-debug-panel__close"
            aria-label="Close panel"
          >
            <X size={20} />
          </button>
        </div>

        {/* Controls */}
        <div className="feature-toggle-debug-panel__controls">
          {/* Search */}
          <div className="feature-toggle-debug-panel__search">
            <input
              type="text"
              placeholder="Search features..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              aria-label="Search feature toggles"
            />
          </div>

          {/* Group Filter */}
          <div className="feature-toggle-debug-panel__filter">
            <select
              value={selectedGroup}
              onChange={(e) => setSelectedGroup(e.target.value)}
              aria-label="Filter by feature group"
            >
              <option value="all">All Groups</option>
              {Object.values(FEATURE_GROUPS).map((group) => (
                <option key={group} value={group}>
                  {group}
                </option>
              ))}
            </select>
          </div>

          <div className="feature-toggle-debug-panel__actions">
            <button
              onClick={handleClearOverrides}
              className="feature-toggle-debug-panel__button feature-toggle-debug-panel__button--clear"
            >
              <RotateCcw size={16} />
              Clear All Overrides
            </button>
          </div>
        </div>

        {/* Feature List */}
        <div className="feature-toggle-debug-panel__content">
          {filteredFeatures.length === 0 ? (
            <div
              style={{ textAlign: "center", padding: "32px", color: "#666" }}
            >
              No features found matching your criteria.
            </div>
          ) : (
            filteredFeatures.map(([group, features]) => (
              <div key={group} className="feature-toggle-debug-panel__group">
                <h3 className="feature-toggle-debug-panel__group-title">
                  {group}
                </h3>
                <div className="feature-toggle-debug-panel__features">
                  {features.map(({ key, meta, enabled }) => {
                    const inputId = `toggle-${meta.key}`;
                    return (
                      <div
                        key={key}
                        className="feature-toggle-debug-panel__feature"
                      >
                        <div className="feature-toggle-debug-panel__feature-info">
                          <div className="feature-toggle-debug-panel__feature-name">
                            {meta.key}
                            <span
                              style={{
                                fontSize: "11px",
                                color: "#888",
                                marginLeft: "8px",
                              }}
                            >
                              (default: {meta.default ? "ON" : "OFF"})
                            </span>
                          </div>
                          <div className="feature-toggle-debug-panel__feature-description">
                            {meta.description}
                          </div>
                        </div>
                        <label
                          htmlFor={inputId}
                          className="feature-toggle-debug-panel__toggle"
                        >
                          <input
                            id={inputId}
                            type="checkbox"
                            checked={enabled}
                            onChange={(e) =>
                              handleToggleFeature(key, e.target.checked)
                            }
                            aria-label={`Toggle feature ${meta.key}`}
                          />
                          <span className="feature-toggle-debug-panel__toggle-slider"></span>
                          <span
                            style={{
                              fontSize: "12px",
                              fontWeight: "500",
                              color: enabled ? "#10b981" : "#6b7280",
                              marginLeft: "8px",
                            }}
                          >
                            {enabled ? "ON" : "OFF"}
                          </span>
                        </label>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        <div className="feature-toggle-debug-panel__footer">
          <div className="feature-toggle-debug-panel__note">
            <Info size={16} />
            <span>
              Changes are stored locally and will persist across sessions. Use
              "Clear All Overrides" to reset to PostHog values.
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
